import { useMemo } from 'react';
import { map } from 'rxjs/operators';
import { useObservableValue } from '../../hooks/useObservable';
import { wsScanToDoubleMap, wsScanToMap } from '../../pipes';
import { SecurityTypesUtils, type SecurityTypes } from '../../types';
import type { Security } from '../../types/Security';
import { EMPTY_ARRAY } from '../../utils/empty';
import { getCurrencyPairFromSecurity, shouldDeleteSecurityFromIndex, type SecurityMemoryObservable } from './utils';

export function usePerpIndexes(securityMemoryObs: SecurityMemoryObservable) {
  const perpsObs = useMemo(
    () =>
      securityMemoryObs.pipe(
        map(({ latestMessage }) => latestMessage),
        wsScanToMap<Security, string, SecurityTypes['Perp'], SecurityTypes['Perp']>({
          getUniqueKey: security => security.Symbol,
          getInsertable: security => security,
          newMapEachUpdate: false,
          isRelevant: SecurityTypesUtils.Perp.isType,
          shouldDelete: shouldDeleteSecurityFromIndex,
        }),
        map(perpsBySymbol => [...perpsBySymbol.values()])
      ),
    [securityMemoryObs]
  );
  const perpsByCurrencyPairObs = useMemo(
    () =>
      securityMemoryObs.pipe(
        map(({ latestMessage }) => latestMessage),
        wsScanToDoubleMap<Security, string, string, SecurityTypes['Perp'], SecurityTypes['Perp']>({
          getKey1: getCurrencyPairFromSecurity,
          getKey2: security => security.Symbol,
          getInsertable: security => security,
          newOuterMapEachUpdate: true,
          newInnerMapsEachUpdate: true,
          isRelevant: SecurityTypesUtils.Perp.isType,
          shouldDelete: shouldDeleteSecurityFromIndex,
          deleteInnerMapOnEmptied: true,
        })
      ),
    [securityMemoryObs]
  );
  const perps = useObservableValue(() => perpsObs, [perpsObs], EMPTY_ARRAY);
  const perpsByCurrencyPair = useObservableValue(() => perpsByCurrencyPairObs, [perpsByCurrencyPairObs], new Map());

  return useMemo(() => ({ perps, perpsByCurrencyPair }), [perps, perpsByCurrencyPair]);
}
